import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { marcaService, Marca } from '../../services/marca';

const MarcaTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Buscar todas as marcas
  const { data: marcas = [], isLoading, error } = useQuery({
    queryKey: ['marcas'],
    queryFn: marcaService.getAll,
  });

  // Filtrar marcas baseado na busca
  const filteredMarcas = useMemo(() => {
    return marcas.filter(marca =>
      marca.nome.toLowerCase().includes(search.toLowerCase())
    );
  }, [marcas, search]);

  // Paginação dos dados filtrados
  const paginatedMarcas = useMemo(() => {
    return filteredMarcas.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredMarcas, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (id: number) => {
    navigate(`/marcas/editar/${id}`);
  };

  const handleNew = () => {
    navigate('/marcas/novo');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar as marcas.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Filtrar"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            },
          }}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
          sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
        >
          Nova Marca
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Marca</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMarcas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Nenhuma marca encontrada para o filtro informado.' : 'Nenhuma marca cadastrada.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedMarcas.map((marca) => (
                <TableRow key={marca.id} hover>
                  <TableCell>{marca.id}</TableCell>
                  <TableCell>{marca.nome}</TableCell>
                  <TableCell>
                    <Chip
                      label={marca.ativo === 'SIM' ? 'ATIVO' : 'INATIVO'}
                      color={marca.ativo === 'SIM' ? 'success' : 'default'}
                      size="small"
                      sx={{ 
                        fontWeight: 'bold',
                        backgroundColor: marca.ativo === 'SIM' ? '#8cbaa0' : 'default.main',
                        color: marca.ativo === 'SIM' ? 'white' : 'text.primary',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(marca.id)}
                      size="small"
                      title="Editar"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredMarcas.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count} itens`
        }
        sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />
    </Paper>
  );
};

export default MarcaTable;
